import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  IconButton,
  Paper,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Cancel,
  Upgrade,
  CheckCircle,
  Warning,
  Settings,
  Add,
  Remove,
  People
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { UseApi } from '../Hooks/UseApi';
import { UseAuthRoles } from '../Hooks/UseAuthRoles';

const SubscriptionManager = () => {
  const api = UseApi();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { getAccessTokenSilently } = useAuth0();
  const { getSubscriptionLevel } = UseAuthRoles();

  const [subscriptionData, setSubscriptionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [cancelDialog, setCancelDialog] = useState(false);
  const [seatsToAdd, setSeatsToAdd] = useState(1);
  const [isProcessingSubscription, setIsProcessingSubscription] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [isYearly, setIsYearly] = useState(false);

  const plans = {
    basic: {
      name: 'Basic',
      monthlyPrice: 35,
      yearlyPrice: 385,
      monthlyPriceId: 'price_1SbDP4P2MQvVS2rX4yotqIke',
      yearlyPriceId: 'price_1SbDP4P2MQvVS2rXtaI60FTt',
      role: 'basic_subscriber',
      maxUsers: 1,
      features: ['Unlimited basic client profiles', 'Unlimited Appointment Scheduling', 'Digital Consent Forms', 'Customizable forms']
    },
    standard: {
      name: 'Standard',
      monthlyPrice: 85,
      yearlyPrice: 935,
      monthlyPriceId: 'price_1SbDP2P2MQvVS2rXvCV9eHIK',
      yearlyPriceId: 'price_1SbDP2P2MQvVS2rXTUSYzeER',
      role: 'standard_subscriber',
      maxUsers: 1,
      features: ['Everything in Basic', 'Advanced Client Profiles (Tattoo History, Allergies, Style Preferences)', 'Appointment Reminders (Email)', 'Booking Enhancements', 'Automated Aftercare emails', 'Inventory Management', 'Low Stock Alerts']
    },
    starter_studio: {
      name: 'Starter Studio',
      monthlyPrice: 199,
      yearlyPrice: 2189,
      monthlyPriceId: 'price_1SbDP1P2MQvVS2rXTMkqz2tZ',
      yearlyPriceId: 'price_1SbDP1P2MQvVS2rXE0eyOjWK',
      role: 'starter_studio_subscriber',
      maxUsers: 4,
      additionalSeatPrice: 25,
      additionalSeatYearlyPrice: 275,
      additionalSeatMonthlyPriceId: 'price_1SbDOxP2MQvVS2rXMwvKjBFd',
      additionalSeatYearlyPriceId: 'price_1SbDOxP2MQvVS2rXnBlH1QLb',
      features: ['All Basic Features (per seat)', 'Shared Client Profiles', 'Studio Dashboard', 'Role-Based Permissions', 'Shared Form Templates', 'Cross-Artist Calendar View', 'Inventory Management', 'Multi-user studio (3 users)', 'Additional seats $25/month']
    },
    growth_studio: {
      name: 'Growth Studio',
      monthlyPrice: 299,
      yearlyPrice: 3289,
      monthlyPriceId: 'price_1SbDOzP2MQvVS2rXl7NLeJOX',
      yearlyPriceId: 'price_1SbDOzP2MQvVS2rXBuFLn3jb',
      role: 'growth_studio_subscriber',
      maxUsers: 4,
      additionalSeatPrice: 40,
      additionalSeatYearlyPrice: 440,
      additionalSeatMonthlyPriceId: 'price_1SbDOtP2MQvVS2rXqeDl6qJx',
      additionalSeatYearlyPriceId: 'price_1SbDOtP2MQvVS2rXbCJg0W2L',
      features: ['Everything in Starter Studio', 'Advanced Client Profiles', 'Automated Appointment Reminders (Email)', 'Studio-wide inventory', 'Automated Aftercare Instructions', 'Additional seats $40/month', 'Priority support']
    }
  };

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    
    if (sessionId) {
      // Handle successful subscription/payment
      handleSuccessfulSubscription(sessionId);
    } else {
      // Normal subscription manager access
      fetchSubscriptionStatus();
    }
  }, [searchParams]);

  const handleSuccessfulSubscription = async (sessionId) => {
    try {
      setIsProcessingSubscription(true);
      setLoading(true);
      // Get fresh token
      const token = await getAccessTokenSilently({
        cacheMode: 'off'
      });
      
      // Call the success endpoint with POST (backend now supports both GET and POST)
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/subscription/success/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          session_id: sessionId
        })
      });

      const data = await response.json();
      if (response.ok && data.success) {
        setSuccess('Subscription updated successfully! Setting up your account...');
        
        // Wait a moment for the success message, then check profile
        setTimeout(async () => {
          await checkUserProfileAndRedirect();
        }, 2000);
      } else {
        // Try to provide helpful error message
        const errorMessage = data.error || data.message || 'Failed to process subscription';
        console.error('❌ Subscription processing failed:', errorMessage);
        setError(`Subscription processing issue: ${errorMessage}. Your payment was successful - please refresh the page or contact support if issues persist.`);
        setIsProcessingSubscription(false);
        setLoading(false);
        
        // Even on error, try to redirect after a delay
        setTimeout(async () => {
          await checkUserProfileAndRedirect();
        }, 5000);
      }
    } catch (error) {
      console.error('❌ Error processing subscription:', error);
      setError('Network error while processing subscription. Your payment was successful - please refresh the page.');
      setIsProcessingSubscription(false);
      setLoading(false);
      
      // Try redirect anyway after delay
      setTimeout(async () => {
        await checkUserProfileAndRedirect();
      }, 5000);
    }
  };

  const checkUserProfileAndRedirect = async () => {
    try {
      setRedirecting(true);
      const token = await getAccessTokenSilently({
        cacheMode: 'off'
      });
      
      // FIRST: Check if user is a studio member - they don't need onboarding
      try {
        const subscriptionResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/subscription/details/`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        
        if (subscriptionResponse.ok) {
          const subData = await subscriptionResponse.json();
          
          // If user is a studio member (not owner), skip onboarding
          if (subData?.is_studio_member && subData?.studio_membership?.role !== 'owner') {
            navigate('/dashboard', { replace: true });
            return;
          }
        }
      } catch (subError) {
        // Continue with normal onboarding check
      }
      
      // Check BOTH user profile AND shop profile (for owners and regular subscribers)
      const [userProfileResponse, shopProfileResponse] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_BASE_URL}/user/profile/`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }),
        fetch(`${process.env.REACT_APP_API_BASE_URL}/shop/profile/`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        })
      ]);
      
      if (userProfileResponse.ok && shopProfileResponse.ok) {
        const userProfileData = await userProfileResponse.json();
        const shopProfileData = await shopProfileResponse.json();
        // Check if user needs onboarding (missing any required fields)
        const needsOnboarding = !userProfileData.first_name?.trim() || 
                               !userProfileData.last_name?.trim() || 
                               !shopProfileData.name?.trim() ||
                               !shopProfileData.address?.trim() ||
                               !shopProfileData.city?.trim() ||
                               !shopProfileData.state?.trim() ||
                               !shopProfileData.zip_code?.trim();
        if (needsOnboarding) {
          navigate('/onboarding', { replace: true });
        } else {
          navigate('/dashboard', { replace: true });
        }
      } else {
        // If profile check fails, assume they need onboarding
        navigate('/onboarding', { replace: true });
      }
    } catch (error) {
      console.error('Error checking profile:', error);
      navigate('/onboarding', { replace: true });
    }
  };

  const fetchSubscriptionStatus = async () => {
    try {
      setLoading(true);
      const data = await api.fetchWithAuth('/subscription/details/');
      setSubscriptionData(data);
      setError(null);
    } catch (err) {
      setError('Failed to load subscription information');
      console.error('Subscription status error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePlanChange = async (newPlan) => {
    try {
      setActionLoading(true);
      setError(null);
      
      const plan = plans[newPlan];
      const priceId = isYearly ? plan.yearlyPriceId : plan.monthlyPriceId;
      
      const checkoutData = {
        plan_id: newPlan,
        mode: 'subscription',
        price_id: priceId,
        success_url: `${window.location.origin}/subscription-manager?session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: `${window.location.origin}/subscription-manager`
      };
      
      const response = await api.fetchWithAuth('/subscription/create-checkout-session/', {
        method: 'POST',
        body: JSON.stringify(checkoutData)
      });
      
      if (response.checkout_url) {
        window.location.href = response.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err) {
      console.error('Plan change error:', err);
      setError('Failed to change subscription plan. Please try using the billing portal.');
      setActionLoading(false);
    }
  };

  const handleAddSeats = async () => {
    try {
      setActionLoading(true);
      setError(null);
      
      const currentPlan = plans[subscriptionData.tier];
      const priceId = isYearly 
        ? currentPlan.additionalSeatYearlyPriceId 
        : currentPlan.additionalSeatMonthlyPriceId;
      
      const checkoutData = {
        line_items: [{
          price: priceId,
          quantity: seatsToAdd
        }],
        success_url: `${window.location.origin}/subscription-manager?session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: `${window.location.origin}/subscription-manager`,
        subscription_data: {
          metadata: {
            additional_seats: seatsToAdd.toString(),
            seat_type: subscriptionData.tier
          }
        }
      };
      
      const response = await api.fetchWithAuth('/subscription/create-seat-checkout/', {
        method: 'POST',
        body: JSON.stringify(checkoutData)
      });
      
      if (response.checkout_url) {
        window.location.href = response.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err) {
      console.error('Add seats error:', err);
      setError('Failed to add additional seats. Please use the billing portal to manage your subscription.');
      setActionLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      setActionLoading(true);
      setError(null);
      
      await api.fetchWithAuth('/subscription/cancel/', {
        method: 'POST'
      });
      
      setSuccess('Subscription will be cancelled at the end of your current billing period');
      setCancelDialog(false);
      await fetchSubscriptionStatus();
    } catch (err) {
      setError('Failed to cancel subscription');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivateSubscription = async () => {
    try {
      setActionLoading(true);
      setError(null);
      
      await api.fetchWithAuth('/subscription/reactivate/', {
        method: 'POST'
      });
      
      setSuccess('Subscription reactivated successfully');
      await fetchSubscriptionStatus();
    } catch (err) {
      setError('Failed to reactivate subscription');
    } finally {
      setActionLoading(false);
    }
  };

  const handleManageBilling = async () => {
    try {
      setActionLoading(true);
      const data = await api.fetchWithAuth('/subscription/portal/', {
        method: 'POST'
      });
      
      window.location.href = data.portal_url;
    } catch (err) {
      setError('Failed to open billing portal');
      setActionLoading(false);
    }
  };

  const getTierLevel = (tier) => {
    const levels = { unpaid: 0, basic: 1, standard: 2, starter_studio: 3, growth_studio: 4 };
    return levels[tier] || 0;
  };

  const getCurrentTier = () => {
    return getTierLevel(subscriptionData?.tier || 'unpaid');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'trialing': return 'info';
      case 'past_due': return 'warning';
      case 'canceled': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return <CheckCircle />;
      case 'trialing': return <CheckCircle />;
      case 'past_due': return <Warning />;
      case 'canceled': return <Cancel />;
      default: return null;
    }
  };

  const isStudioPlan = (tier) => {
    return ['starter_studio', 'growth_studio'].includes(tier);
  };

  const formatPrice = (plan) => {
    if (isYearly) {
      return `$${plan.yearlyPrice}/year`;
    }
    return `$${plan.monthlyPrice}/month`;
  };

  const getAdditionalSeatPrice = (plan) => {
    if (isYearly) {
      return plan.additionalSeatYearlyPrice || 0;
    }
    return plan.additionalSeatPrice || 0;
  };

  // Show processing screen when handling subscription success
  if (isProcessingSubscription || redirecting) {
    return (
      <Container maxWidth="md">
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center', 
          minHeight: '60vh',
          textAlign: 'center'
        }}>
          {isProcessingSubscription && (
            <>
              <Box sx={{ fontSize: '60px', mb: 2 }}>🎉</Box>
              <Typography variant="h4" gutterBottom color="success.main">
                Subscription Successful!
              </Typography>
              <Typography variant="h6" sx={{ mb: 3, color: 'text.secondary' }}>
                Welcome to InkedUp Manager! Your subscription is now active.
              </Typography>
            </>
          )}
          
          {redirecting && (
            <>
              <Box sx={{ fontSize: '60px', mb: 2 }}>🚀</Box>
              <Typography variant="h5" gutterBottom>
                Setting up your account...
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                Checking your profile and redirecting you to the right place.
              </Typography>
            </>
          )}
          
          <CircularProgress size={40} sx={{ mb: 2 }} />
          <Typography variant="body2" color="text.secondary">
            This should only take a moment...
          </Typography>
          
          {error && (
            <Alert severity="warning" sx={{ mt: 3, maxWidth: 500 }}>
              {error}
            </Alert>
          )}
        </Box>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4 }}>
        <Typography variant="h4" gutterBottom>
          Subscription Management
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>{success}</Alert>}

        <Grid container spacing={3}>
          {/* Current Subscription Card */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Current Subscription
                </Typography>
                
                {subscriptionData?.has_subscription ? (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h5" sx={{ mr: 2 }}>
                        {plans[subscriptionData.tier]?.name || 'Unknown Plan'}
                      </Typography>
                      <Chip
                        icon={getStatusIcon(subscriptionData.status)}
                        label={subscriptionData.status?.toUpperCase()}
                        color={getStatusColor(subscriptionData.status)}
                        size="small"
                      />
                    </Box>
                    
                    <Typography variant="h4" color="primary" gutterBottom>
                      ${plans[subscriptionData.tier]?.monthlyPrice || 0}/month
                    </Typography>

                    {/* Studio-specific info */}
                    {isStudioPlan(subscriptionData.tier) && subscriptionData.subscription_details && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                        <Typography variant="body2" color="textSecondary">
                          <People sx={{ fontSize: 16, mr: 1, verticalAlign: 'text-bottom' }} />
                          {subscriptionData.subscription_details.total_users_allowed} total seats 
                          ({subscriptionData.subscription_details.base_users_included} included + {subscriptionData.subscription_details.additional_seats} additional)
                        </Typography>
                        {subscriptionData.subscription_details.monthly_additional_cost > 0 && (
                          <Typography variant="body2" color="textSecondary">
                            Additional seats cost: ${subscriptionData.subscription_details.monthly_additional_cost}/month
                          </Typography>
                        )}
                      </Box>
                    )}
                    
                    {subscriptionData.next_billing_date && (
                      <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                        Next billing: {new Date(subscriptionData.next_billing_date).toLocaleDateString()}
                      </Typography>
                    )}
                    
                    {subscriptionData.cancel_at_period_end && (
                      <Alert severity="warning" sx={{ mt: 2 }}>
                        Your subscription will end {subscriptionData.current_period_end 
                        ? `on ${new Date(subscriptionData.current_period_end).toLocaleDateString()}`
                        : 'at the end of your billing period'}
                      </Alert>
                    )}
                  </>
                ) : (
                  <>
                    <Typography variant="h5" gutterBottom>
                      No Active Subscription
                    </Typography>
                    <Typography variant="body1" color="textSecondary">
                      Choose a plan to get started with premium features
                    </Typography>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Quick Actions Card */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Quick Actions
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {subscriptionData?.has_subscription && (
                    <>
                      <Button
                        variant="outlined"
                        startIcon={<Settings />}
                        onClick={handleManageBilling}
                        disabled={actionLoading}
                        fullWidth
                      >
                        Manage Billing & Payment Methods
                      </Button>

                      {/* Add Seats for Studio Plans */}
                      {isStudioPlan(subscriptionData.tier) && (
                        <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Add Additional Seats
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <IconButton 
                              onClick={() => setSeatsToAdd(Math.max(1, seatsToAdd - 1))}
                              size="small"
                            >
                              <Remove />
                            </IconButton>
                            <Typography variant="h6" sx={{ minWidth: 40, textAlign: 'center' }}>
                              {seatsToAdd}
                            </Typography>
                            <IconButton 
                              onClick={() => setSeatsToAdd(seatsToAdd + 1)}
                              size="small"
                            >
                              <Add />
                            </IconButton>
                            <Typography variant="body2" color="textSecondary" sx={{ ml: 1 }}>
                              +${seatsToAdd * getAdditionalSeatPrice(plans[subscriptionData.tier])}/{isYearly ? 'year' : 'month'}
                            </Typography>
                          </Box>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={handleAddSeats}
                            disabled={actionLoading}
                            fullWidth
                          >
                            Add {seatsToAdd} Seat{seatsToAdd > 1 ? 's' : ''}
                          </Button>
                          <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                            This will redirect to Stripe for secure payment processing
                          </Typography>
                        </Paper>
                      )}
                      
                      {subscriptionData.cancel_at_period_end ? (
                        <Button
                          variant="contained"
                          color="success"
                          startIcon={<CheckCircle />}
                          onClick={handleReactivateSubscription}
                          disabled={actionLoading}
                          fullWidth
                        >
                          Reactivate Subscription
                        </Button>
                      ) : (
                        <Button
                          variant="outlined"
                          color="error"
                          startIcon={<Cancel />}
                          onClick={() => setCancelDialog(true)}
                          disabled={actionLoading}
                          fullWidth
                        >
                          Cancel Subscription
                        </Button>
                      )}
                    </>
                  )}
                  
                  {!subscriptionData?.has_subscription && (
                    <Button
                      variant="contained"
                      onClick={() => navigate('/subscription')}
                      fullWidth
                    >
                      Choose a Plan
                    </Button>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Available Plans */}
          {subscriptionData?.has_subscription && (
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">
                      Change Plan
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={isYearly}
                          onChange={(e) => setIsYearly(e.target.checked)}
                          color="primary"
                        />
                      }
                      label={isYearly ? "Yearly (Save ~8%)" : "Monthly" }
                    />
                  </Box>
                  
                  <Grid container spacing={2}>
                    {Object.entries(plans).map(([planId, plan]) => (
                      <Grid item xs={12} md={3} key={planId}>
                        <Card 
                          variant="outlined"
                          sx={{ 
                            position: 'relative',
                            border: subscriptionData.tier === planId ? 2 : 1,
                            borderColor: subscriptionData.tier === planId ? 'primary.main' : 'divider'
                          }}
                        >
                          <CardContent>
                            {subscriptionData.tier === planId && (
                              <Chip
                                label="CURRENT"
                                color="primary"
                                size="small"
                                sx={{ position: 'absolute', top: 8, right: 8 }}
                              />
                            )}
                            
                            <Typography variant="h6" gutterBottom>
                              {plan.name}
                            </Typography>
                            <Typography variant="h5" color="primary" gutterBottom>
                              {formatPrice(plan)}
                            </Typography>

                            {plan.maxUsers > 1 && (
                              <Typography variant="body2" color="textSecondary" gutterBottom>
                                Up to {plan.maxUsers} users included
                              </Typography>
                            )}
                            
                            <Box sx={{ mb: 2 }}>
                              {plan.features.slice(0, 3).map((feature, idx) => (
                                <Typography key={idx} variant="body2" color="textSecondary">
                                  • {feature}
                                </Typography>
                              ))}
                            </Box>
                            
                            {subscriptionData.tier !== planId && (
                              <Button
                                variant="contained"
                                fullWidth
                                onClick={() => handlePlanChange(planId)}
                                disabled={actionLoading}
                                startIcon={
                                  getTierLevel(planId) > getCurrentTier() ? <Upgrade /> : <CheckCircle />
                                }
                              >
                                {getTierLevel(planId) > getCurrentTier() ? 'Upgrade' : 'Downgrade'}
                              </Button>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                  
                  <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      <strong>Plan Changes:</strong> Upgrades and downgrades are handled securely through Stripe. 
                      You'll be redirected to complete the change. Alternatively, use the "Manage Billing" button above 
                      to access the full Stripe customer portal.
                    </Typography>
                  </Alert>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>

        {/* Cancel Confirmation Dialog */}
        <Dialog open={cancelDialog} onClose={() => setCancelDialog(false)}>
          <DialogTitle>Cancel Subscription</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to cancel your subscription? You'll retain access to all features 
              until the end of your current billing period ({subscriptionData?.current_period_end ? 
              new Date(subscriptionData.current_period_end).toLocaleDateString() : 'end of period'}).
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCancelDialog(false)}>
              Keep Subscription
            </Button>
            <Button 
              onClick={handleCancelSubscription} 
              color="error"
              disabled={actionLoading}
            >
              {actionLoading ? <CircularProgress size={20} /> : 'Cancel Subscription'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default SubscriptionManager;